import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";

import {
  type MemoryFeedbackType,
  type MemoryItemExplanationResponse,
  type MemoryListItem,
  explainMemoryItem,
  forgetMemoryItem,
  listMemoryItems,
  pinMemoryItem,
  revokeMemoryItem,
  submitMemoryFeedback,
  unpinMemoryItem,
} from "../../services/api";

type StatusFilterMode = "active" | "all";
type PinnedFilterMode = "all" | "pinned" | "unpinned";

const KNOWN_ITEM_TYPES = [
  "preference",
  "project_context",
  "goal",
  "constraint",
  "relationship_hint",
  "procedure_hint",
];

const HISTORY_STATUSES = ["active", "stale", "superseded", "revoked", "soft_deleted"];

const FEEDBACK_ACTIONS: Array<{ value: MemoryFeedbackType; label: string }> = [
  { value: "helpful", label: "Helpful" },
  { value: "incorrect", label: "Incorrect" },
  { value: "not_important", label: "Not important" },
  { value: "always_remember", label: "Always remember" },
];

function toErrorMessage(err: unknown): string {
  return err instanceof Error ? err.message : "Request failed.";
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function formatRatio(value: number): string {
  if (!Number.isFinite(value)) return "-";
  return `${Math.round(value * 100)}%`;
}

function statusColorScheme(status: string): string {
  switch (status) {
    case "active":
      return "green";
    case "stale":
      return "yellow";
    case "superseded":
      return "purple";
    case "revoked":
      return "red";
    case "soft_deleted":
      return "gray";
    default:
      return "blue";
  }
}

export function MemoryManager() {
  const toast = useToast();
  const panelBg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const selectedCardBg = useColorModeValue("pink.50", "rgba(236, 72, 153, 0.2)");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.300");

  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [items, setItems] = useState<MemoryListItem[]>([]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detail, setDetail] = useState<MemoryItemExplanationResponse | null>(null);

  const [statusMode, setStatusMode] = useState<StatusFilterMode>("active");
  const [pinnedMode, setPinnedMode] = useState<PinnedFilterMode>("all");
  const [itemTypeFilter, setItemTypeFilter] = useState<string>("all");
  const [scopeFilter, setScopeFilter] = useState<string>("all");
  const [query, setQuery] = useState("");

  const [refreshKey, setRefreshKey] = useState(0);
  const [pendingByItemId, setPendingByItemId] = useState<Record<string, boolean>>({});

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const response = await listMemoryItems({
        status: statusMode === "all" ? HISTORY_STATUSES : ["active"],
        itemType: itemTypeFilter === "all" ? undefined : [itemTypeFilter],
        scope: scopeFilter === "all" ? undefined : [scopeFilter],
        pinned:
          pinnedMode === "all"
            ? undefined
            : pinnedMode === "pinned"
            ? true
            : false,
        limit: 200,
        offset: 0,
      });
      setItems(response.items || []);
    } catch (err) {
      setListError(toErrorMessage(err));
    } finally {
      setListLoading(false);
    }
  }, [itemTypeFilter, pinnedMode, scopeFilter, statusMode]);

  const loadDetail = useCallback(async (itemId: string) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await explainMemoryItem(itemId);
      setDetail(response);
    } catch (err) {
      setDetail(null);
      setDetailError(toErrorMessage(err));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList, refreshKey]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    if (!items.some((item) => item.id === selectedId)) {
      setSelectedId(null);
      setDetail(null);
      return;
    }
    void loadDetail(selectedId);
  }, [items, loadDetail, selectedId]);

  const typeOptions = useMemo(() => {
    const set = new Set<string>(KNOWN_ITEM_TYPES);
    items.forEach((item) => set.add(item.item_type));
    return Array.from(set).sort();
  }, [items]);

  const scopeOptions = useMemo(() => {
    const set = new Set<string>();
    items.forEach((item) => set.add(item.scope));
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const haystack = [item.title || "", item.content_text, item.item_type, item.scope].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [items, query]);

  const withPending = useCallback(
    async (itemId: string, action: () => Promise<void>) => {
      setPendingByItemId((prev) => ({ ...prev, [itemId]: true }));
      try {
        await action();
      } finally {
        setPendingByItemId((prev) => ({ ...prev, [itemId]: false }));
      }
    },
    []
  );

  const refreshAfterAction = useCallback(
    async (itemId: string, successTitle: string, successDescription?: string) => {
      toast({
        status: "success",
        title: successTitle,
        description: successDescription,
        duration: 2200,
        isClosable: true,
      });
      if (selectedId === itemId) {
        await loadDetail(itemId);
      }
      setRefreshKey((value) => value + 1);
    },
    [loadDetail, selectedId, toast]
  );

  const handlePinToggle = useCallback(async () => {
    if (!detail?.item.id) return;
    const itemId = detail.item.id;
    await withPending(itemId, async () => {
      try {
        if (detail.explainability.is_pinned) {
          await unpinMemoryItem(itemId);
          await refreshAfterAction(itemId, "Memory unpinned");
          return;
        }
        await pinMemoryItem(itemId);
        await refreshAfterAction(itemId, "Memory pinned");
      } catch (err) {
        toast({
          status: "error",
          title: "Could not update pin",
          description: toErrorMessage(err),
          duration: 3200,
          isClosable: true,
        });
      }
    });
  }, [detail, refreshAfterAction, toast, withPending]);

  const handleRevoke = useCallback(async () => {
    if (!detail?.item.id) return;
    if (!window.confirm("Revoke this memory? It will be excluded from active retrieval.")) {
      return;
    }
    const itemId = detail.item.id;
    await withPending(itemId, async () => {
      try {
        await revokeMemoryItem(itemId);
        await refreshAfterAction(itemId, "Memory revoked");
      } catch (err) {
        toast({
          status: "error",
          title: "Could not revoke memory",
          description: toErrorMessage(err),
          duration: 3200,
          isClosable: true,
        });
      }
    });
  }, [detail, refreshAfterAction, toast, withPending]);

  const handleForget = useCallback(async () => {
    if (!detail?.item.id) return;
    if (!window.confirm("Forget this memory now?")) {
      return;
    }
    const itemId = detail.item.id;
    await withPending(itemId, async () => {
      try {
        await forgetMemoryItem(itemId);
        await refreshAfterAction(itemId, "Memory forgotten");
      } catch (err) {
        toast({
          status: "error",
          title: "Could not forget memory",
          description: toErrorMessage(err),
          duration: 3200,
          isClosable: true,
        });
      }
    });
  }, [detail, refreshAfterAction, toast, withPending]);

  const handleFeedback = useCallback(
    async (feedbackType: MemoryFeedbackType) => {
      if (!detail?.item.id) return;
      const itemId = detail.item.id;
      await withPending(itemId, async () => {
        try {
          await submitMemoryFeedback(itemId, { feedback_type: feedbackType });
          await refreshAfterAction(itemId, `Feedback saved: ${feedbackType.replace("_", " ")}`);
        } catch (err) {
          toast({
            status: "error",
            title: "Could not save feedback",
            description: toErrorMessage(err),
            duration: 3200,
            isClosable: true,
          });
        }
      });
    },
    [detail, refreshAfterAction, toast, withPending]
  );

  const selectedPending = selectedId ? Boolean(pendingByItemId[selectedId]) : false;

  return (
    <VStack align="stretch" spacing={4}>
      <Stack direction={{ base: "column", md: "row" }} spacing={3}>
        <Select
          value={statusMode}
          onChange={(e) => setStatusMode(e.target.value as StatusFilterMode)}
          maxW={{ base: "100%", md: "220px" }}
        >
          <option value="active">Active only</option>
          <option value="all">Include history</option>
        </Select>
        <Select
          value={pinnedMode}
          onChange={(e) => setPinnedMode(e.target.value as PinnedFilterMode)}
          maxW={{ base: "100%", md: "220px" }}
        >
          <option value="all">Pinned: all</option>
          <option value="pinned">Pinned only</option>
          <option value="unpinned">Unpinned only</option>
        </Select>
        <Select
          value={itemTypeFilter}
          onChange={(e) => setItemTypeFilter(e.target.value)}
          maxW={{ base: "100%", md: "240px" }}
        >
          <option value="all">Type: all</option>
          {typeOptions.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
        <Select
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value)}
          maxW={{ base: "100%", md: "220px" }}
        >
          <option value="all">Scope: all</option>
          {scopeOptions.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search memory text"
          maxW={{ base: "100%", md: "320px" }}
        />
      </Stack>

      <Grid templateColumns={{ base: "1fr", lg: "1fr 1.2fr" }} gap={4}>
        <Box bg={panelBg} borderRadius="xl" p={3} borderWidth="1px" minH="420px">
          <HStack justify="space-between" mb={3}>
            <Text fontWeight="semibold">Memory items</Text>
            <Button size="sm" variant="ghost" onClick={() => setRefreshKey((value) => value + 1)}>
              Refresh
            </Button>
          </HStack>

          {listLoading ? (
            <Flex align="center" justify="center" minH="180px" direction="column" gap={2}>
              <Spinner size="lg" />
              <Text color="gray.500">Loading memory...</Text>
            </Flex>
          ) : null}

          {!listLoading && listError ? (
            <VStack align="stretch" spacing={3}>
              <Text color="red.400">{listError}</Text>
              <Button onClick={() => setRefreshKey((value) => value + 1)} size="sm" alignSelf="flex-start">
                Retry
              </Button>
            </VStack>
          ) : null}

          {!listLoading && !listError && filteredItems.length === 0 ? (
            <Box borderWidth="1px" borderStyle="dashed" borderRadius="lg" p={4} color="gray.500">
              No memory items match these filters.
            </Box>
          ) : null}

          {!listLoading && !listError && filteredItems.length > 0 ? (
            <VStack align="stretch" spacing={2} maxH="640px" overflowY="auto" pr={1}>
              {filteredItems.map((item) => {
                const selected = selectedId === item.id;
                return (
                  <Box
                    key={item.id}
                    borderWidth="1px"
                    borderRadius="lg"
                    p={3}
                    bg={selected ? selectedCardBg : cardBg}
                    cursor="pointer"
                    onClick={() => setSelectedId(item.id)}
                    _hover={{ borderColor: "pink.300" }}
                  >
                    <HStack justify="space-between" mb={1}>
                      <Text fontWeight="semibold" noOfLines={1}>
                        {item.title || item.content_text}
                      </Text>
                      {item.is_pinned ? <Badge colorScheme="yellow">Pinned</Badge> : null}
                    </HStack>
                    {!item.title ? (
                      <Text fontSize="sm" color="gray.500" noOfLines={2}>
                        {item.content_text}
                      </Text>
                    ) : null}
                    <HStack mt={2} spacing={2} flexWrap="wrap">
                      <Badge colorScheme="blue">{item.item_type}</Badge>
                      <Badge colorScheme="cyan">{item.scope}</Badge>
                      <Badge colorScheme={statusColorScheme(item.status)}>{item.status}</Badge>
                    </HStack>
                    <Text mt={2} fontSize="xs" color="gray.500">
                      Updated {formatDate(item.updated_at)}
                    </Text>
                  </Box>
                );
              })}
            </VStack>
          ) : null}
        </Box>

        <Box bg={panelBg} borderRadius="xl" p={4} borderWidth="1px" minH="420px">
          {!selectedId ? (
            <Flex minH="340px" align="center" justify="center">
              <Text color="gray.500">Select a memory item to inspect explainability and controls.</Text>
            </Flex>
          ) : null}

          {selectedId && detailLoading ? (
            <Flex align="center" justify="center" minH="300px" direction="column" gap={2}>
              <Spinner size="lg" />
              <Text color="gray.500">Loading details...</Text>
            </Flex>
          ) : null}

          {selectedId && !detailLoading && detailError ? (
            <VStack align="stretch" spacing={3}>
              <Text color="red.400">{detailError}</Text>
              <Button size="sm" onClick={() => void loadDetail(selectedId)} alignSelf="flex-start">
                Retry
              </Button>
            </VStack>
          ) : null}

          {selectedId && !detailLoading && !detailError && detail ? (
            <VStack align="stretch" spacing={4}>
              <Box>
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={1}>
                    <Text fontSize="lg" fontWeight="bold">
                      {detail.item.title || detail.item.content_text}
                    </Text>
                    <Text color="gray.500" fontSize="sm">
                      {detail.item.title ? detail.item.content_text : ""}
                    </Text>
                  </VStack>
                  {detail.explainability.is_pinned ? <Badge colorScheme="yellow">Pinned</Badge> : null}
                </HStack>
                <HStack mt={2} spacing={2} flexWrap="wrap">
                  <Badge colorScheme="blue">{detail.item.item_type}</Badge>
                  <Badge colorScheme="cyan">{detail.item.scope}</Badge>
                  <Badge colorScheme={statusColorScheme(detail.item.status)}>{detail.item.status}</Badge>
                  <Badge colorScheme="gray">{detail.item.sensitivity}</Badge>
                </HStack>
              </Box>

              <HStack flexWrap="wrap" spacing={2}>
                <Button size="sm" onClick={() => void handlePinToggle()} isLoading={selectedPending}>
                  {detail.explainability.is_pinned ? "Unpin" : "Pin"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="orange"
                  onClick={() => void handleForget()}
                  isLoading={selectedPending}
                >
                  Forget
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={() => void handleRevoke()}
                  isLoading={selectedPending}
                  isDisabled={detail.item.status === "revoked" || detail.item.status === "soft_deleted"}
                >
                  Revoke
                </Button>
                {FEEDBACK_ACTIONS.map((action) => (
                  <Button
                    key={action.value}
                    size="sm"
                    variant="ghost"
                    onClick={() => void handleFeedback(action.value)}
                    isLoading={selectedPending}
                  >
                    {action.label}
                  </Button>
                ))}
              </HStack>

              <Divider />

              <Stack direction={{ base: "column", md: "row" }} spacing={5}>
                <VStack align="stretch" spacing={1} flex="1">
                  <Text fontSize="sm" color="gray.500">
                    Confidence {formatRatio(detail.item.confidence)}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Importance {formatRatio(detail.item.importance)}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Stability {formatRatio(detail.item.stability)}
                  </Text>
                </VStack>
                <VStack align="stretch" spacing={1} flex="1">
                  <Text fontSize="sm" color="gray.500">
                    Updated {formatDate(detail.item.updated_at)}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Last confirmed {formatDate(detail.item.last_confirmed_at)}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Expires {formatDate(detail.item.expires_at)}
                  </Text>
                </VStack>
              </Stack>

              <Divider />

              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Why this is remembered
                </Text>
                <VStack align="stretch" spacing={1}>
                  <Text fontSize="sm" color="gray.500">
                    Lifecycle: {detail.explainability.lifecycle_reason}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Evidence count: {detail.explainability.evidence_count}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Feedback count: {detail.explainability.feedback_count}
                  </Text>
                </VStack>
              </Box>

              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Evidence
                </Text>
                {detail.evidence.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">
                    No evidence rows were attached yet.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={2} maxH="240px" overflowY="auto">
                    {detail.evidence.map((row) => (
                      <Box key={row.evidence_id} borderWidth="1px" borderRadius="md" p={3} bg={cardBg}>
                        <HStack justify="space-between" mb={1}>
                          <Badge colorScheme="purple">{row.evidence_type}</Badge>
                          <Text fontSize="xs" color="gray.500">
                            {formatDate(row.event_created_at)}
                          </Text>
                        </HStack>
                        <Text fontSize="sm" color={secondaryTextColor}>
                          {row.content_text}
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Event {row.event_type}
                        </Text>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>

              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Lineage
                </Text>
                <VStack align="stretch" spacing={2}>
                  <Text fontSize="sm" color="gray.500">
                    Supersedes: {detail.explainability.lineage.supersedes?.id || "-"}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Superseded by: {detail.explainability.lineage.superseded_by.length}
                  </Text>
                </VStack>
              </Box>

              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Feedback trail
                </Text>
                {detail.feedback.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">
                    No feedback records yet.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={2} maxH="220px" overflowY="auto">
                    {detail.feedback.map((entry) => (
                      <HStack key={entry.id} justify="space-between" borderWidth="1px" borderRadius="md" p={2}>
                        <HStack spacing={2}>
                          <Badge colorScheme="pink">{entry.feedback_type}</Badge>
                          <Text fontSize="sm" color="gray.500" noOfLines={1}>
                            {entry.notes || "No notes"}
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                          {formatDate(entry.created_at)}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          ) : null}
        </Box>
      </Grid>
    </VStack>
  );
}

export default MemoryManager;
